// CANVAS-CORE (A14) + CANVAS-BLOCKS: Reports › Live, the Workspace. An agent's `show_user` (MCP),
// the voice front or the viewer places blocks, and each block is the app's own thing drawn in
// place with the app's own components: a board's real columns (or list) with the block's filter,
// cards that open in the task drawer, move between columns and take comments; a calendar's real
// day, week or month grid whose events open in the event dialog; a doc's body read-only in the
// page renderer; a seat's KPI tiles; an image or a task's images through the authenticated file
// proxy (a click opens the file preview); a video playing inline; a thread's messages in the
// chat's message list. Nothing is an iframe, an embedded screen or a card that links out. The
// server serves only blocks the viewer may read; each block then reads its subject under the
// viewer's own session, and a block it is refused (403 / 404) renders nothing, never an error.
// What the person does on a block (opens a card, moves it, plays a video) is told to the agent
// (`interact.ts`). The page is talk-first: the voice slot on top, the blocks in the centre, a
// chat panel docked beside them, and the steps strip with the runs' latest steps.
import { ApiError } from "@companyos/sdk";
import {
  BoardView,
  type BoardViewColumn,
  Button,
  ButtonGroup,
  type ChatMessage,
  cn,
  Input,
  ListView,
  LocationHeader,
  MessageList,
  PageEditor,
  ScoreMark,
  Sparkline,
  StatGrid,
  StatTile,
  type TagChipColor,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ActivityIcon,
  CalendarIcon,
  FileTextIcon,
  ImageIcon,
  KanbanSquareIcon,
  LinkIcon,
  MessageSquareIcon,
  MicIcon,
  SendIcon,
  TargetIcon,
  VideoIcon,
  WorkflowIcon,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Api } from "../../api/client";
import type { CalendarOccurrence } from "../../api/types";
import {
  type CalendarViewKind,
  daysOf,
  colorOf as paletteOf,
  windowOf,
  ymd,
} from "../calendar/calendar-model";
import { calendarsQuery, occurrencesQuery, selectionOf } from "../calendar/calendar-queries";
import { type CalendarActions, MonthGrid, TimeGrid } from "../calendar/calendar-views";
import {
  ConfirmDelete,
  EventDetailDialog,
  EventFormDialog,
  type EventFormSeed,
  ScopeDialog,
  useEventWrites,
} from "../calendar/event-dialogs";
import { orgChartQuery } from "../chart/chart-queries";
import { latestMessagesQuery } from "../chat/chat-queries";
import { hasStructure, MessageBlocks } from "../chat/message-blocks";
import type { CompanyAccess } from "../company";
import { formatMetricValue, formatRecent, formatTarget } from "../format";
import { hitMiss } from "../hit-miss";
import { useAsk } from "../insights/ask-host";
import { companyQuery, seatQuery, seatScorecardQuery } from "../queries";
import { Loading, usePeriod } from "../screen-parts";
import { type FileItem, fileKeys, PreviewDialog, useBlobUrl, useBoardCovers } from "../work/files";
import { useTaskWrites } from "../work/inline-editors";
import { localDay, statusTone, subtaskCounts, toSummary, useSeatPeople } from "../work/model";
import { TaskDrawer } from "../work/task-drawer";
import type { TaskCard } from "../work/view-engine";
import { boardAllTasksQuery, boardQuery, pageQuery } from "../work/work-queries";
import {
  type CanvasBlock,
  type CanvasState,
  filterCards,
  type LiveCanvasOp,
  type LiveRunStep,
} from "./canvas-state";
import { ChainDiagram, chainRefused, useChain } from "./chain-block";
import {
  DeskSurface,
  Dock,
  type Ghost,
  type RenderBlock,
  useDesk,
  useWindowChrome,
} from "./desk-surface";
import { DockedChat } from "./docked-chat";
import { type CanvasInteract, useCanvasInteract, useDeskWrite } from "./interact";
import type { LiveCanvas } from "./live-channel";
import { useLivePushToTalk, useVoiceReady } from "./push-to-talk";
import { SessionStrip } from "./timeline/session-timeline";
import { VoicePanel } from "./voice-panel";

/** A block's reads: a refusal is the viewer's permission, said by leaving the block out. */
const QUIET = { quietStatuses: [403, 404] };
const refused = (e: unknown) => e instanceof ApiError && (e.status === 403 || e.status === 404);

export function CanvasScreen({
  api,
  company,
  live,
  state,
  views,
}: {
  api: Api;
  company: CompanyAccess;
  live: Pick<LiveCanvas, "sessionId" | "steps" | "streaming"> & Partial<Pick<LiveCanvas, "ops">>;
  state: CanvasState;
  /** CANVAS-BLOCKS: the Live page's view tabs (Workspace | Browser | Computer), in the header. */
  views?: ReactNode;
}) {
  const interact = useCanvasInteract(api, company.id, live.sessionId);
  // LIVE-WORKSPACE: one DE for the page: the voice panel talks to it, the dock shows its DM.
  const [deId, setDeId] = useState("");
  const [voiceOpen, setVoiceOpen] = useState(false);
  // LIVE-DESKTOP: the blocks are windows on a desk (desk-surface.tsx).
  const write = useDeskWrite(api, company.id, live.sessionId);
  const desk = useDesk({
    companyId: company.id,
    sessionId: live.sessionId,
    state,
    ops: live.ops ?? NO_OPS,
    interact,
    write,
  });
  // Hold Space to talk (not while the voice panel has its own mic open).
  const ptt = useLivePushToTalk({
    api,
    company,
    sessionId: live.sessionId,
    deId: deId || undefined,
    enabled: !voiceOpen,
  });
  // The empty desk's suggestions open by the person's own hand (`liveCanvas.op`, its session).
  const onGhost = async (g: Ghost) => {
    if (api.mode !== "http") return;
    const co = company.id;
    let block: CanvasBlock = { id: "calendar", kind: "calendar", ref: {} };
    if (g !== "calendar") {
      const boards = await api.client.boards.list({ params: { co } }).catch(() => null);
      const b = boards?.data[0];
      if (!b) return;
      block =
        g === "today"
          ? {
              id: "today",
              kind: "board",
              title: "Today",
              ref: { board_id: b.id },
              filter: { due: "today" },
            }
          : { id: "board", kind: "board", ref: { board_id: b.id } };
    }
    await api.client.liveCanvas
      .op({
        params: { co },
        body: { session_id: live.sessionId, op: { op: "show", blocks: [block] } },
      })
      .catch(() => undefined);
  };
  const renderBlock: RenderBlock = (block, o) => (
    <BlockView
      api={api}
      company={company}
      block={block}
      itemId={o.itemId}
      note={o.note}
      large={o.large}
      interact={interact}
    />
  );
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="live-canvas">
      <LocationHeader
        icon={ActivityIcon}
        crumbs={[{ label: "Reports" }]}
        title="Live"
        count={state.blocks.length}
        actions={views}
      />
      <div className="flex min-w-0 gap-5 px-6 py-4 max-xl:flex-col max-md:px-4">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <DeskSurface
            desk={desk}
            blocks={state.blocks}
            highlight={state.highlight}
            renderBlock={renderBlock}
            onGhost={(g) => void onGhost(g)}
          />
          <Dock desk={desk} blocks={state.blocks}>
            {/* The mic pill: Text | Voice; the voice panel (voice-panel.tsx) opens under it. */}
            <div data-slot="voice-slot" className="flex shrink-0 items-center gap-2">
              {ptt.listening || ptt.hint ? (
                <span
                  role="status"
                  data-slot="ptt-hint"
                  className="rounded-md bg-muted px-2 py-1 text-xs text-foreground"
                >
                  {ptt.listening ? "Listening… let go of Space to send." : ptt.hint}
                </span>
              ) : null}
              <TalkSwitch
                api={api}
                company={company}
                voiceOpen={voiceOpen}
                onVoice={() => setVoiceOpen((o) => !o)}
              />
            </div>
          </Dock>
          {voiceOpen ? (
            <section
              aria-label="Voice"
              className="rounded-xl border border-border bg-card px-4 py-3"
            >
              <VoicePanel
                api={api}
                company={company}
                sessionId={live.sessionId}
                deId={deId || undefined}
                onDeChange={setDeId}
                onClose={() => setVoiceOpen(false)}
                // LANE-C: the card the canvas is on (its chain), read back from the op log after
                // a reload, so typed words keep the target the voice turns had.
                focusTaskId={
                  (state.blocks.find((b) => b.kind === "chain")?.ref.task_id as
                    | string
                    | undefined) ?? null
                }
              />
            </section>
          ) : null}
        </div>
        <div className="flex w-96 shrink-0 flex-col gap-5 max-xl:w-full">
          <DockedChat
            api={api}
            company={company}
            deId={deId}
            onDeChange={setDeId}
            fallback={<WorkspaceChat company={company} />}
          />
          <StepsStrip steps={live.steps} streaming={live.streaming} />
        </div>
      </div>
      <SessionStrip api={api} company={company} sessionId={live.sessionId} />
    </div>
  );
}

const NO_OPS: LiveCanvasOp[] = [];

type BlockProps = {
  api: Api;
  company: CompanyAccess;
  block: CanvasBlock;
  itemId: string | null;
  note: string | null;
  large: boolean;
  interact: CanvasInteract;
};

function BlockView(p: BlockProps) {
  switch (p.block.kind) {
    case "board":
      return <BoardBlock {...p} />;
    case "calendar":
      return <CalendarBlock {...p} />;
    case "doc":
      return <DocBlock {...p} />;
    case "image_grid":
      return <ImageGridBlock {...p} />;
    case "image":
      return <ImageBlock {...p} />;
    case "video":
      return <VideoBlock {...p} />;
    case "kpi":
      return <KpiBlock {...p} />;
    case "chain":
      return <ChainBlock {...p} />;
    case "transcript":
      return <TranscriptBlock {...p} />;
  }
}

/** The card around a block: icon, title, an "open" link, the highlight's note, the body. */
function BlockCard({
  icon: Icon,
  title,
  open,
  note,
  large,
  children,
}: {
  icon: typeof ActivityIcon;
  title: string;
  open?: ReactNode;
  note: string | null;
  large?: boolean;
  children: ReactNode;
}) {
  // LIVE-DESKTOP: in a window the header is the title bar (drag handle, window buttons) and the
  // body fills the window.
  const chrome = useWindowChrome();
  if (chrome)
    return (
      <section
        className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-md"
        aria-label={title}
        data-slot="desk-window"
      >
        <header
          className={cn(
            "flex min-w-0 items-center gap-2 border-b border-border bg-muted/50 py-1 pr-1 pl-3",
            // Tablet: a taller bar that does not scroll the page under a finger (touch-none).
            chrome.tablet && "min-h-11 touch-none",
            !chrome.phone && `${chrome.handleClass} cursor-grab select-none active:cursor-grabbing`,
          )}
        >
          <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          <h2 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold">{title}</h2>
          {open ? <span className="desk-nodrag flex shrink-0">{open}</span> : null}
          {chrome.controls}
        </header>
        {note ? (
          <p className="mx-3 mt-2 rounded-md bg-primary-soft px-2 py-1 text-xs text-primary-text">
            {note}
          </p>
        ) : null}
        <div className="min-h-0 min-w-0 flex-1 overflow-auto p-3">{children}</div>
      </section>
    );
  return (
    <section
      className="flex h-full min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4"
      aria-label={title}
    >
      <header className="flex min-w-0 items-center gap-2">
        <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        <h2 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold">{title}</h2>
        {open}
      </header>
      {note ? (
        <p className="rounded-md bg-primary-soft px-2 py-1 text-xs text-primary-text">{note}</p>
      ) : null}
      <div className={cn("min-w-0 overflow-auto", large ? "max-h-[640px]" : "max-h-80")}>
        {children}
      </div>
    </section>
  );
}

const openClass =
  "inline-flex shrink-0 items-center gap-1 text-xs text-primary-text hover:underline";

function Muted({ children }: { children: ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

/** Ring the highlighted item inside a block the app's components draw. */
const LIT_ITEM =
  "[&_[data-highlighted=true]]:rounded-lg [&_[data-highlighted=true]]:ring-2 [&_[data-highlighted=true]]:ring-primary";

/**
 * The highlight's item marked inside a block the app's components draw (they take no
 * highlight of their own): `data-highlighted` on the element whose `attr` is the item's id,
 * after every render (the items come and go as the reads answer).
 */
function useMarkItem(root: RefObject<HTMLElement | null>, attr: string, itemId: string | null) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    for (const x of el.querySelectorAll(`[${attr}][data-highlighted]`))
      if (x.getAttribute(attr) !== itemId) x.removeAttribute("data-highlighted");
    if (!itemId || !/^[\w-]+$/.test(itemId)) return;
    for (const x of el.querySelectorAll(`[${attr}="${itemId}"]`))
      x.setAttribute("data-highlighted", "true");
  });
}

/** Once per block: the person saw its subject (a doc read, a KPI looked at). */
function useViewed(ready: boolean, interact: CanvasInteract, blockId: string, detail: string) {
  const told = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || told.current === blockId) return;
    told.current = blockId;
    interact({ action: "view", block_id: blockId, detail });
  }, [ready, interact, blockId, detail]);
}

// Board ------------------------------------------------------------------------------------------

/** Pages of the board's tasks read for one block (the board screen's own "every task" read). */
const BOARD_PAGES = 10;

/**
 * The task drawer's composer says nothing to its host when a comment is sent; a send from the
 * drawer (Enter in a composer with text, or its send button) is the comment.
 */
function isCommentSend(e: SyntheticEvent): boolean {
  const t = e.target;
  if (!(t instanceof Element)) return false;
  if (e.type === "keydown") {
    const k = e as unknown as KeyboardEvent;
    return (
      k.key === "Enter" && !k.shiftKey && Boolean(t.closest("[data-slot=composer][data-has-text]"))
    );
  }
  const button = t.closest("[data-slot=composer] button");
  if (!button) return false;
  const name = `${button.getAttribute("aria-label") ?? ""} ${button.textContent ?? ""}`;
  return /\b(send|comment|post|reply)\b/i.test(name) && !/later/i.test(name);
}

// Board: the board's real columns (or its list), the block's filter applied, read with the board
// screen's queries so change pings refresh it; a card opens in the task drawer in place, moves
// between columns, and takes comments there.
function BoardBlock({ api, company, block, itemId, note, large, interact }: BlockProps) {
  const co = company.id;
  const id = block.ref.board_id as string;
  const board = useQuery({ ...boardQuery(api, co, id), meta: QUIET });
  const tasks = useInfiniteQuery({
    ...boardAllTasksQuery(api, co, id),
    meta: QUIET,
    enabled: board.isSuccess,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = tasks;
  const pages = tasks.data?.pages.length ?? 0;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && pages < BOARD_PAGES) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, pages]);
  const people = useSeatPeople(api, co);
  const member = block.filter?.assignee_membership_id ?? null;
  const chart = useQuery({ ...orgChartQuery(api, co), meta: QUIET, enabled: member !== null });
  const assigneeSeats = useMemo(
    () =>
      member && chart.data
        ? new Set(
            chart.data.seats
              .filter((s) => s.holders.some((h) => h.membership_id === member))
              .map((s) => s.id),
          )
        : null,
    [member, chart.data],
  );
  const writes = useTaskWrites(api, company);
  const listView = block.filter?.view === "list";
  const covers = useBoardCovers(api, co, id, board.isSuccess && !listView);
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  useMarkItem(root, "data-task-id", itemId);
  if (refused(board.error) || refused(tasks.error)) return null;

  const all = tasks.data?.pages.flatMap((p) => p.data) ?? [];
  const cards = filterCards(all, block.filter, new Date(), assigneeSeats);
  const byId = new Map(all.map((t) => [t.id, t] as const));
  const b = board.data;
  const today = localDay(new Date());
  const subtasks = subtaskCounts(all, b);
  const summary = (t: TaskCard) => {
    const s = toSummary(t, { board: b, people, today, subtasks });
    const c = listView ? undefined : covers.get(t.id);
    return c?.length ? { ...s, covers: c } : s;
  };
  const statuses = (b?.status_set ?? []).filter(
    (s) => !block.filter?.status || s.key === block.filter.status,
  );
  const title = (taskId: string) => byId.get(taskId)?.title;
  const openTask = (taskId: string) => {
    setOpen(taskId);
    interact({ action: "open", block_id: block.id, item_id: taskId, detail: title(taskId) });
  };
  const closeTask = () => {
    if (open) interact({ action: "close", block_id: block.id, item_id: open, detail: title(open) });
    setOpen(null);
  };
  const moveTo = (taskId: string, status: string) => {
    const t = byId.get(taskId);
    if (!t || t.status === status) return;
    writes.setStatus(taskId, status);
    const column = b?.status_set.find((s) => s.key === status)?.name ?? status;
    interact({
      action: "move",
      block_id: block.id,
      item_id: taskId,
      detail: `${t.title} → ${column}`,
    });
  };
  const onComment = (e: SyntheticEvent) => {
    if (open && isCommentSend(e))
      interact({ action: "comment", block_id: block.id, item_id: open, detail: title(open) });
  };

  return (
    <BlockCard
      icon={KanbanSquareIcon}
      title={block.title ?? b?.name ?? "Board"}
      note={note}
      large={large}
    >
      <div ref={root} className={LIT_ITEM} data-slot="canvas-board">
        {board.isPending || tasks.isPending ? (
          <Loading label="Loading the board" rows={3} />
        ) : board.isError || tasks.isError || !b ? (
          <Muted>The board could not be loaded.</Muted>
        ) : listView ? (
          <ListView
            aria-label={`${b.name} list`}
            groups={statuses.map((s) => ({
              id: s.key,
              title: s.name,
              tasks: cards.filter((t) => t.status === s.key).map(summary),
            }))}
            {...(open ? { activeTaskId: open } : {})}
            onTaskOpen={openTask}
            onToggleDone={(taskId, done) => {
              const status = done
                ? b.status_set.find((s) => s.done)?.key
                : b.status_set.find((s) => !s.done)?.key;
              if (status) moveTo(taskId, status);
            }}
            onTaskDrop={(taskId, group) => moveTo(taskId, group)}
            empty={<Muted>No cards match.</Muted>}
          />
        ) : (
          <BoardView
            aria-label={`${b.name} board`}
            columns={statuses.map(
              (s): BoardViewColumn => ({
                id: s.key,
                title: s.name,
                tone: statusTone(s),
                tasks: cards.filter((t) => t.status === s.key).map(summary),
              }),
            )}
            {...(open ? { selectedTaskId: open } : {})}
            onTaskOpen={openTask}
            onTaskMove={(taskId, column) => moveTo(taskId, column)}
            empty={<Muted>No cards match.</Muted>}
          />
        )}
      </div>
      {/* The drawer is portalled; its events still bubble here through React. */}
      <div className="contents" onKeyDownCapture={onComment} onClickCapture={onComment}>
        <TaskDrawer
          api={api}
          company={company}
          taskId={open}
          onClose={closeTask}
          onOpenTask={openTask}
        />
      </div>
    </BlockCard>
  );
}

// Calendar ---------------------------------------------------------------------------------------

type CalendarModal =
  | { kind: "detail"; o: CalendarOccurrence }
  | { kind: "form"; seed: EventFormSeed }
  | { kind: "delete"; o: CalendarOccurrence };

// Calendar: the calendar screen's own time grid (day, week) or month grid for the asked range,
// read with its queries; an event opens in the event dialog in place (edit, duplicate, delete
// there too), and a drag moves a one-off event.
function CalendarBlock({ api, company, block, itemId, note, large, interact }: BlockProps) {
  const co = company.id;
  const view: CalendarViewKind = block.ref.calendar_view ?? "day";
  const anchor = block.ref.date ?? ymd(new Date());
  const cals = useQuery({ ...calendarsQuery(api, co), meta: QUIET });
  const weekStart = cals.data?.week_start ?? 1;
  const days = useMemo(() => daysOf(view, anchor, weekStart), [view, anchor, weekStart]);
  const win = windowOf(days);
  const selection = selectionOf(cals.data?.data ?? []);
  const occ = useQuery({
    ...occurrencesQuery(api, co, win.from, win.to, selection),
    meta: QUIET,
    enabled: cals.isSuccess,
  });
  const writes = useEventWrites(api, co);
  const [modal, setModal] = useState<CalendarModal | null>(null);
  const root = useRef<HTMLDivElement>(null);
  useMarkItem(root, "data-event-id", itemId);
  const byId = useMemo(
    () => new Map((cals.data?.data ?? []).map((c) => [c.id, c] as const)),
    [cals.data],
  );
  if (refused(cals.error) || refused(occ.error)) return null;

  const colorOf = (cid: string): TagChipColor => paletteOf(cid, byId);
  const occurrences = (occ.data?.data ?? []).filter((o) => {
    if (block.ref.calendar_id && o.calendar_id !== block.ref.calendar_id) return false;
    const c = byId.get(o.calendar_id);
    return !c || c.shown || o.attending;
  });
  const today = ymd(new Date());
  const none = () => undefined;
  const actions: CalendarActions = {
    open: (o) => {
      if (o.busy) return;
      setModal({ kind: "detail", o });
      interact({
        action: "open",
        block_id: block.id,
        item_id: o.event_id,
        detail: o.title ?? "Event",
      });
    },
    edit: none,
    duplicate: none,
    remove: (o) => setModal({ kind: "delete", o }),
    copyLink: none,
    slot: none,
    task: none,
    showDay: none,
    item: none,
    moveItem: none,
    move: (o, times) => {
      if (o.recurring) return;
      writes.move.mutate({ o, times, scope: null });
      interact({ action: "move", block_id: block.id, item_id: o.event_id, detail: o.title ?? "" });
    },
  };
  const shared = { occurrences, tasks: null, items: [], colorOf, today, actions };
  const label =
    view === "day"
      ? new Date(`${anchor}T12:00:00`).toLocaleDateString()
      : view === "week"
        ? `week of ${new Date(`${days[0] ?? anchor}T12:00:00`).toLocaleDateString()}`
        : new Date(`${anchor}T12:00:00`).toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
          });

  return (
    <BlockCard
      icon={CalendarIcon}
      title={block.title ?? `Calendar · ${label}`}
      note={note}
      large={large}
    >
      <div
        ref={root}
        className={cn("flex min-h-0 flex-col", LIT_ITEM)}
        data-slot="canvas-calendar"
        data-view={view}
      >
        {cals.isPending || occ.isPending ? (
          <Loading label="Loading the calendar" rows={3} />
        ) : cals.isError || occ.isError ? (
          <Muted>The calendar could not be loaded.</Muted>
        ) : view === "month" ? (
          <MonthGrid {...shared} days={days} anchor={anchor} />
        ) : (
          <TimeGrid {...shared} days={days} companyZone={company.calendar.timezone} />
        )}
      </div>
      {modal?.kind === "detail" ? (
        <EventDetailDialog
          api={api}
          company={company}
          occurrence={modal.o}
          color={colorOf(modal.o.calendar_id)}
          onClose={() => {
            interact({ action: "close", block_id: block.id, item_id: modal.o.event_id });
            setModal(null);
          }}
          onEdit={(detail) =>
            setModal({ kind: "form", seed: { mode: "edit", occurrence: modal.o, detail } })
          }
          onDuplicate={(detail) => setModal({ kind: "form", seed: { mode: "duplicate", detail } })}
          onDelete={() => setModal({ kind: "delete", o: modal.o })}
          onCopyLink={none}
        />
      ) : null}
      {modal?.kind === "form" ? (
        <EventFormDialog
          api={api}
          company={company}
          calendars={cals.data?.data ?? []}
          myCalendarId={cals.data?.my_calendar_id ?? null}
          myMembershipId={cals.data?.my_membership_id ?? null}
          seed={modal.seed}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal?.kind === "delete" ? (
        modal.o.recurring ? (
          <ScopeDialog
            verb="Delete"
            onClose={() => setModal(null)}
            onPick={(scope) => {
              writes.remove.mutate({ o: modal.o, scope });
              setModal(null);
            }}
          />
        ) : (
          <ConfirmDelete
            title={modal.o.title ?? "this event"}
            onClose={() => setModal(null)}
            onConfirm={() => {
              writes.remove.mutate({ o: modal.o });
              setModal(null);
            }}
          />
        )
      ) : null}
    </BlockCard>
  );
}

// Doc --------------------------------------------------------------------------------------------

// Doc: the page's body in the page renderer (the page screen's editor), read-only.
function DocBlock({ api, company, block, note, large, interact }: BlockProps) {
  const id = block.ref.page_id as string;
  const page = useQuery({ ...pageQuery(api, company.id, id), meta: QUIET });
  const title = block.title ?? page.data?.title ?? "Doc";
  useViewed(page.isSuccess, interact, block.id, title);
  if (refused(page.error)) return null;
  return (
    <BlockCard icon={FileTextIcon} title={title} note={note} large={large}>
      {page.isPending ? (
        <Loading label="Loading the doc" rows={3} />
      ) : page.isError ? (
        <Muted>The doc could not be loaded.</Muted>
      ) : (
        <div data-slot="canvas-doc">
          <PageEditor
            key={`${page.data.id}:${page.data.version}`}
            initialBlocks={page.data.body}
            editable={false}
            width="full"
            size="drawer"
            label={`${page.data.title} body`}
          />
        </div>
      )}
    </BlockCard>
  );
}

// Files: images and videos -----------------------------------------------------------------------

const fileMetaKey = (co: string, id: string) => ["company", co, "work", "file", id] as const;

/** One file's record (name, kind, thumbnail), read quietly. */
function useFileMeta(api: Api, co: string, id: string) {
  return useQuery({
    queryKey: fileMetaKey(co, id),
    queryFn: () => api.client.files.get({ params: { co, id } }),
    meta: QUIET,
  });
}

// Image: the file through the authenticated proxy (never a public URL); a click opens the file
// preview (the work files' viewer) over the page.
function ImageBlock({ api, company, block, itemId, note, large, interact }: BlockProps) {
  const co = company.id;
  const id = block.ref.file_id as string;
  const meta = useFileMeta(api, co, id);
  const blob = useBlobUrl(api, co, meta.isSuccess ? id : null);
  const [shown, setShown] = useState<FileItem | null>(null);
  if (refused(meta.error)) return null;
  const f = meta.data;
  return (
    <BlockCard
      icon={ImageIcon}
      title={block.title ?? f?.file_name ?? "Image"}
      note={note}
      large={large}
    >
      {meta.isPending ? (
        <Loading label="Loading the image" rows={2} />
      ) : meta.isError || !f ? (
        <Muted>The image could not be loaded.</Muted>
      ) : (
        <button
          type="button"
          data-file-id={f.id}
          data-highlighted={f.id === itemId ? "true" : undefined}
          aria-label={`Open ${f.file_name}`}
          className={cn(
            "flex w-full items-center justify-center overflow-hidden rounded-md border border-border-subtle bg-sunken",
            f.id === itemId && "ring-2 ring-primary",
          )}
          onClick={() => {
            setShown(f);
            interact({ action: "open", block_id: block.id, item_id: f.id, detail: f.file_name });
          }}
        >
          {blob.url ? (
            <img src={blob.url} alt={f.file_name} className="max-h-[560px] w-full object-contain" />
          ) : (
            <ImageIcon aria-hidden="true" className="my-10 size-6 text-muted-foreground" />
          )}
        </button>
      )}
      <PreviewDialog api={api} co={co} file={shown} onClose={() => setShown(null)} />
    </BlockCard>
  );
}

// Video: plays inline, its bytes through the authenticated proxy; the thumbnail is the poster.
function VideoBlock({ api, company, block, itemId, note, large, interact }: BlockProps) {
  const co = company.id;
  const id = block.ref.file_id as string;
  const meta = useFileMeta(api, co, id);
  const blob = useBlobUrl(api, co, meta.isSuccess ? id : null);
  const poster = useBlobUrl(api, co, meta.data?.has_thumb ? id : null, true);
  if (refused(meta.error)) return null;
  const f = meta.data;
  const tell = (action: "play" | "pause") =>
    interact({ action, block_id: block.id, item_id: id, detail: f?.file_name });
  return (
    <BlockCard
      icon={VideoIcon}
      title={block.title ?? f?.file_name ?? "Video"}
      note={note}
      large={large}
    >
      {meta.isPending ? (
        <Loading label="Loading the video" rows={2} />
      ) : meta.isError || !f ? (
        <Muted>The video could not be loaded.</Muted>
      ) : blob.url ? (
        <video
          src={blob.url}
          {...(poster.url ? { poster: poster.url } : {})}
          controls
          playsInline
          preload="metadata"
          aria-label={f.file_name}
          data-file-id={f.id}
          data-highlighted={f.id === itemId ? "true" : undefined}
          className={cn("w-full rounded-md bg-muted", f.id === itemId && "ring-2 ring-primary")}
          onPlay={() => tell("play")}
          onPause={() => tell("pause")}
        />
      ) : blob.error ? (
        <Muted>The video could not be loaded.</Muted>
      ) : (
        <Loading label="Loading the video" rows={2} />
      )}
    </BlockCard>
  );
}

// Image grid: a task's image files (the task's files query), each through the file proxy; a click
// opens the same preview as an image block.
function ImageGridBlock({ api, company, block, itemId, note, large, interact }: BlockProps) {
  const co = company.id;
  const taskId = block.ref.task_id as string;
  const files = useQuery({
    queryKey: fileKeys.live(co, taskId),
    queryFn: () =>
      api.client.files.list({ params: { co }, query: { task_id: taskId, limit: 200 } }),
    meta: QUIET,
  });
  const [shown, setShown] = useState<FileItem | null>(null);
  if (refused(files.error)) return null;
  const images = (files.data?.data ?? []).filter((f) => f.mime.startsWith("image/"));
  return (
    <BlockCard icon={ImageIcon} title={block.title ?? "Images"} note={note} large={large}>
      {files.isPending ? (
        <Loading label="Loading the images" rows={2} />
      ) : files.isError ? (
        <Muted>The images could not be loaded.</Muted>
      ) : images.length === 0 ? (
        <Muted>No images on this task.</Muted>
      ) : (
        <ul className="grid grid-cols-3 gap-2">
          {images.map((f) => (
            <li key={f.id}>
              <ImageTile
                api={api}
                co={co}
                id={f.id}
                name={f.file_name}
                lit={f.id === itemId}
                onOpen={() => {
                  setShown(f);
                  interact({
                    action: "open",
                    block_id: block.id,
                    item_id: f.id,
                    detail: f.file_name,
                  });
                }}
              />
            </li>
          ))}
        </ul>
      )}
      <PreviewDialog api={api} co={co} file={shown} onClose={() => setShown(null)} />
    </BlockCard>
  );
}

function ImageTile({
  api,
  co,
  id,
  name,
  lit,
  onOpen,
}: {
  api: Api;
  co: string;
  id: string;
  name: string;
  lit: boolean;
  onOpen: () => void;
}) {
  const { url } = useBlobUrl(api, co, id, true);
  return (
    <button
      type="button"
      data-file-id={id}
      data-highlighted={lit ? "true" : undefined}
      aria-label={`Open ${name}`}
      onClick={onOpen}
      className={cn(
        "flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-border-subtle bg-sunken",
        lit && "ring-2 ring-primary",
      )}
    >
      {url ? (
        <img src={url} alt={name} className="size-full object-cover" />
      ) : (
        <ImageIcon aria-hidden="true" className="size-5 text-muted-foreground" />
      )}
    </button>
  );
}

// KPI --------------------------------------------------------------------------------------------

// KPI: the seat's scorecard as KPI tiles (value, goal, hit or miss, the trend's sparkline).
function KpiBlock({ api, company, block, note, large, interact }: BlockProps) {
  const co = company.id;
  const id = block.ref.seat_id as string;
  const seat = useQuery({ ...seatQuery(api, co, id), meta: QUIET });
  const week = usePeriod();
  const card = useQuery({
    ...seatScorecardQuery(api, co, id, week),
    meta: QUIET,
    enabled: seat.isSuccess,
  });
  const detail = useQuery({ ...companyQuery(api, co), meta: QUIET });
  const title = block.title ?? seat.data?.title ?? "KPIs";
  useViewed(card.isSuccess, interact, block.id, title);
  if (refused(seat.error) || refused(card.error)) return null;
  const currency = detail.data?.reporting_currency ?? card.data?.cost?.currency ?? "USD";
  const metrics = card.data?.metrics ?? [];
  return (
    <BlockCard icon={TargetIcon} title={title} note={note} large={large}>
      {seat.isPending || card.isPending ? (
        <Loading label="Loading the KPIs" rows={2} />
      ) : seat.isError || card.isError ? (
        <Muted>The KPIs could not be loaded.</Muted>
      ) : metrics.length === 0 ? (
        <Muted>No KPI on this seat yet.</Muted>
      ) : (
        <StatGrid dense aria-label={`${title}, week ${week}`} className="grid-cols-2">
          {metrics.map((m) => {
            const state = hitMiss(m.direction, m.actual, m.target, m.target_max);
            return (
              <StatTile
                key={m.metric_def_id}
                size="sm"
                label={m.name}
                value={m.actual === null ? "—" : formatMetricValue(m.actual, m.unit, currency)}
                detail={
                  <span className="flex flex-col gap-1">
                    <span className="flex items-center gap-2">
                      <ScoreMark state={state} />
                      {m.target === null ? "No goal" : `Goal ${formatTarget(m, currency)}`}
                    </span>
                    {m.trend.length ? (
                      <Sparkline
                        label={`${m.name}, last ${m.trend.length} weeks`}
                        values={m.trend.map((p) => p.actual)}
                        reference={m.trend.map((p) => p.target)}
                      />
                    ) : null}
                  </span>
                }
              />
            );
          })}
        </StatGrid>
      )}
    </BlockCard>
  );
}

// Chain: requester → DE → helpers → delivery, live (chain-block.tsx).
function ChainBlock({ api, company, block, note, large }: BlockProps) {
  const taskId = block.ref.task_id as string;
  const chain = useChain(api, company.id, taskId);
  if (chainRefused(chain.error)) return null;
  return (
    <BlockCard
      icon={WorkflowIcon}
      title={block.title ?? "Chain"}
      note={note}
      large={large}
      open={<TaskLink slug={company.slug} taskId={taskId} />}
    >
      {chain.isPending ? (
        <Loading label="Loading the chain" rows={2} />
      ) : chain.isError ? (
        <Muted>The chain could not be loaded.</Muted>
      ) : (
        <ChainDiagram view={chain.data} />
      )}
    </BlockCard>
  );
}

// Transcript -------------------------------------------------------------------------------------

// Transcript: the thread's latest messages in the chat's own message list, bodies through the
// chat's block renderer.
function TranscriptBlock({ api, company, block, itemId, note, large }: BlockProps) {
  const thread = block.ref.thread_id as string;
  const messages = useQuery({ ...latestMessagesQuery(api, company.id, thread), meta: QUIET });
  const people = useSeatPeople(api, company.id);
  if (refused(messages.error)) return null;
  const rows = (messages.data?.data ?? []).filter((m) => !m.deleted_at && m.body_text.trim());
  const shown: ChatMessage[] = [...rows]
    .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
    .slice(large ? -30 : -8)
    .map((m) => {
      const who = people.nameOf(m.author_membership_id);
      return {
        id: m.id,
        author: {
          id: m.author_membership_id ?? m.author_kind,
          name: who.name,
          kind: m.author_kind === "person" ? "person" : "agent",
        },
        createdAt: m.created_at,
        body: hasStructure(m.body) ? (
          <MessageBlocks body={m.body} />
        ) : (
          <span className="whitespace-pre-wrap break-words">{m.body_text}</span>
        ),
        ...(m.edited_at ? { edited: true } : {}),
      };
    });
  return (
    <BlockCard
      icon={MessageSquareIcon}
      title={block.title ?? "Transcript"}
      note={note}
      large={large}
    >
      {messages.isPending ? (
        <Loading label="Loading the transcript" rows={3} />
      ) : messages.isError ? (
        <Muted>The transcript could not be loaded.</Muted>
      ) : shown.length === 0 ? (
        <Muted>No messages yet.</Muted>
      ) : (
        <MessageList
          messages={shown}
          label={block.title ?? "Transcript"}
          {...(itemId ? { highlightId: itemId } : {})}
        />
      )}
    </BlockCard>
  );
}

function TaskLink({ slug, taskId }: { slug: string; taskId: string }) {
  return (
    <Link to="/c/$slug/work/t/$taskId" params={{ slug, taskId }} className={openClass}>
      <LinkIcon aria-hidden="true" className="size-3" />
      Task
    </Link>
  );
}

/**
 * The chat docked beside the Workspace. Ask's thread lives in its own sheet (it is not a panel
 * a page can hold), so this is the composer that hands the words to it: Ask for owners and
 * admins, the app's Chat for anyone else.
 */
function WorkspaceChat({ company }: { company: CompanyAccess }) {
  const ask = useAsk();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = text.trim();
    if (ask) ask(q || undefined);
    else void navigate({ to: "/c/$slug/chat", params: { slug: company.slug } });
    setText("");
  };
  return (
    <aside
      aria-label="Chat"
      data-slot="workspace-chat"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
    >
      <h2 className="flex items-center gap-2 font-heading text-sm font-semibold">
        <MessageSquareIcon aria-hidden="true" className="size-4 text-muted-foreground" />
        Chat
      </h2>
      <p className="text-sm text-muted-foreground">
        {ask
          ? "Rather type? Ask about the company's numbers and the answer opens beside you."
          : "Rather type? Open Chat to write to the team and its agents."}
      </p>
      <form onSubmit={onSubmit} className="flex items-center gap-2">
        {ask ? (
          <Input
            aria-label="Your question"
            placeholder="Ask about spend, tasks…"
            value={text}
            maxLength={500}
            onChange={(e) => setText(e.target.value)}
          />
        ) : null}
        <Button type="submit" size={ask ? "icon" : "sm"} aria-label={ask ? "Ask" : "Open Chat"}>
          {ask ? <SendIcon aria-hidden="true" /> : "Open Chat"}
        </Button>
      </form>
    </aside>
  );
}

/** The runs' latest steps, newest first, as the live channel sends them. */
function StepsStrip({ steps, streaming }: { steps: readonly LiveRunStep[]; streaming: boolean }) {
  return (
    <aside aria-label="Live steps" data-slot="live-steps" className="flex w-full flex-col gap-2">
      <h2 className="flex items-center gap-2 font-heading text-sm font-semibold">
        Steps
        <span
          className={cn("size-2 rounded-full", streaming ? "bg-success" : "bg-muted-foreground/40")}
          role="img"
          aria-label={streaming ? "Live" : "Reconnecting"}
          title={streaming ? "Live" : "Polling until the stream is back"}
        />
      </h2>
      {steps.length === 0 ? (
        <p className="text-sm text-muted-foreground">No steps yet.</p>
      ) : (
        <ol className="flex flex-col divide-y divide-border-subtle rounded-md border border-border">
          {steps.map((s) => (
            <li
              key={`${s.run_id}:${s.seq}`}
              data-step={`${s.run_id}:${s.seq}`}
              className="px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-medium">{s.tool ?? s.kind}</span>
                <span className="shrink-0 text-xs text-faint">{formatRecent(s.at)}</span>
              </div>
              {s.summary ? (
                <p className="truncate text-xs text-muted-foreground">{s.summary}</p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

/**
 * Text: the docked chat's composer (LIVE-WORKSPACE), else the app's chat surface (Ask, over the
 * page, for owners and admins; Chat for anyone else). Voice: opens the voice front's panel in
 * place (CANVAS-VOICE, voice-panel.tsx): voice when its keys are set, else it says "voice not
 * configured" and what is missing, and text works in the same panel.
 */
function TalkSwitch({
  api,
  company,
  voiceOpen,
  onVoice,
}: {
  api: Api;
  company: CompanyAccess;
  voiceOpen: boolean;
  onVoice: () => void;
}) {
  const ask = useAsk();
  const navigate = useNavigate();
  const toText = () => {
    const dock = document.querySelector<HTMLElement>(
      '[data-slot="workspace-chat"][data-docked="true"]',
    );
    if (dock) {
      dock.scrollIntoView({ block: "nearest" });
      dock.querySelector<HTMLElement>('[contenteditable="true"], textarea')?.focus();
      return;
    }
    if (ask) ask();
    else void navigate({ to: "/c/$slug/chat", params: { slug: company.slug } });
  };
  // W32-LIVE-FIX: Voice is offered only while the voice service answers with its audio slots set
  // and the company's `voice_front` switch is on (useVoiceReady). Otherwise Text is the mode and
  // Voice is shown disabled with the reason, never selected while it can do nothing.
  const { ready: voiceReady, reason } = useVoiceReady(api, company);
  const voiceOn = voiceOpen && voiceReady;
  return (
    <div className="flex flex-col items-end gap-1">
      <ButtonGroup aria-label="Talk to the workspace">
        <Button
          variant={voiceOn ? "outline" : "default"}
          size="lg"
          aria-pressed={!voiceOn}
          onClick={() => {
            if (voiceOpen) onVoice();
            toText();
          }}
        >
          <MessageSquareIcon aria-hidden="true" />
          Text
        </Button>
        <Button
          variant={voiceOn ? "default" : "outline"}
          size="lg"
          aria-pressed={voiceOn}
          aria-expanded={voiceOn}
          disabled={!voiceReady}
          title={voiceReady ? undefined : reason}
          onClick={onVoice}
        >
          <MicIcon aria-hidden="true" />
          Voice
        </Button>
      </ButtonGroup>
      {voiceReady ? null : (
        <span data-slot="voice-unavailable" className="text-xs text-muted-foreground">
          {reason}
        </span>
      )}
    </div>
  );
}
